/* eslint-disable max-len */
import type { Route } from 'nextjs-routes';

const TEMPLATE_MAP: Record<Route['pathname'], string> = {
  '/': 'Explore %network_title% blocks, transactions, addresses, tokens, and network stats in real time.',
  '/txs': 'Browse the latest validated and pending transactions on %network_title%.',
  '/txs/kettle/[hash]': 'View kettle %hash% transactions on %network_title%.',
  '/tx/[hash]': 'View transaction %hash% details, status, fee, and token transfers on %network_title%.',
  '/blocks': 'Browse the latest blocks produced on %network_title%, including size, transactions, and rewards.',
  '/block/[height_or_hash]': 'View transactions, token transfers, and details for block %height_or_hash% on %network_title%.',
  '/block/countdown': 'Estimate when a future %network_title% block will be produced.',
  '/block/countdown/[height]': 'Countdown to %network_title% block %height%.',
  '/accounts': 'Discover top accounts and balances on %network_title%.',
  '/accounts/label/[slug]': 'Search %network_title% addresses by public label.',
  '/address/[hash]': 'View balance, transactions, tokens, and activity for address %hash% on %network_title%.',
  '/verified-contracts': 'Look up verified smart contracts on %network_title%.',
  '/signed-authorizations': 'Browse EIP-7702 signed authorizations on %network_title%.',
  '/contract-verification': 'Verify and publish a smart contract on %network_title%.',
  '/address/[hash]/contract-verification': 'Verify the smart contract at %hash% on %network_title%.',
  '/tokens': 'Explore ERC-20, ERC-721, and ERC-1155 tokens on %network_title%.',
  '/token/[hash]': 'View token details, holders, transfers, and analytics for %hash% on %network_title%.',
  '/token/[hash]/instance/[id]': 'View NFT instance details for token %hash% on %network_title%.',
  '/apps': 'Explore DApps and marketplace apps on %network_title%.',
  '/apps/[id]': 'Open a marketplace app on %network_title%.',
  '/stats': 'Track %network_title% network charts and on-chain insights.',
  '/stats/[id]': 'View the %network_title% chart and historical metrics.',
  '/api-docs': 'Read %network_title% explorer API documentation for developers.',
  '/graphiql': 'Query %network_title% data with GraphQL.',
  '/search-results': 'Search results on %network_title% explorer.',
  '/auth/profile': 'Manage your %network_title% explorer profile.',
  '/account/rewards': 'View account rewards on %network_title% explorer.',
  '/account/watchlist': 'Manage your %network_title% address watchlist.',
  '/account/api-key': 'Manage API keys for %network_title% explorer.',
  '/account/custom-abi': 'Manage custom ABIs on %network_title% explorer.',
  '/account/tag-address': 'Manage private address tags on %network_title% explorer.',
  '/account/verified-addresses': 'Manage verified addresses on %network_title% explorer.',
  '/public-tags/submit': 'Propose a new public tag for your address, contract or set of contracts for your dApp. Our team will review and approve your submission. Public tags are incredible tool which helps users identify contracts and addresses.',
  '/withdrawals': 'Track withdrawals on %network_title%.',
  '/visualize/sol2uml': 'Generate a Solidity UML diagram on %network_title% explorer.',
  '/csv-export': 'Export %network_title% blockchain data to CSV.',
  '/developers/csv-export': 'Download %network_title% transactions, transfers, and holders as CSV.',
  '/deposits': 'Track L1 to L2 deposits on %network_title%.',
  '/output-roots': 'Browse output roots on %network_title%.',
  '/dispute-games': 'Browse dispute games on %network_title%.',
  '/batches': 'Browse transaction batches on %network_title%.',
  '/batches/[number]': 'View L2 transaction batch %number% on %network_title%.',
  '/blobs/[hash]': 'View blob %hash% details on %network_title%.',
  '/ops': 'Browse ERC-4337 user operations on %network_title%.',
  '/bundles': 'Browse ERC-4337 bundle transactions on %network_title%.',
  '/op/[hash]': 'View user operation %hash% details on %network_title%.',
  '/404': 'The requested page was not found on %network_title% explorer.',
  '/name-domains': 'Browse name domains on %network_title%.',
  '/name-domains/[name]': 'View domain details for %name% on %network_title%.',
  '/validators': 'Browse validators on %network_title%.',
  '/gas-tracker': 'Explore real-time %network_title% gas fees with accurate %network_gwei% estimates and live transaction costs.',
  '/mud-worlds': 'Browse MUD worlds on %network_title%.',
  '/token-transfers': 'Browse recent token transfers on %network_title%.',
  '/advanced-filter': 'Filter %network_title% transactions and transfers with advanced criteria.',
  '/pools': 'Browse DEX pools on %network_title%.',
  '/pools/[hash]': 'View pool details on %network_title%.',

  // service routes, added only to make typescript happy
  '/login': 'Log in to %network_title% explorer.',
  '/sprite': 'SVG sprite for %network_title% explorer.',
  '/api/metrics': '%network_title% node API prometheus metrics.',
  '/api/monitoring/invalid-api-schema': '%network_title% node API monitoring.',
  '/api/log': '%network_title% node API request log.',
  '/api/media-type': '%network_title% node API media type.',
  '/api/proxy': '%network_title% node API proxy.',
  '/api/csrf': '%network_title% node API CSRF token.',
  '/api/healthz': '%network_title% node API health check.',
  '/api/config': '%network_title% node API app config.',
  '/api/robots': '%network_title% node API robots.txt.',
  '/api/sitemap': '%network_title% node API sitemap.',
  '/api/sprite': '%network_title% node API SVG sprite content.',
};

const TEMPLATE_MAP_ENHANCED: Partial<Record<Route['pathname'], string>> = {
  '/stats/[id]': '%description%',
};

export function make(pathname: Route['pathname'], isEnriched = false) {
  return (isEnriched ? TEMPLATE_MAP_ENHANCED[pathname] : undefined) ?? TEMPLATE_MAP[pathname] ?? '';
}
